/**
 * Reads a delivery list out of a CSV or Excel file entirely in the browser —
 * nothing is uploaded until the driver has matched the columns and confirmed.
 * Every cell is normalized to a trimmed string; the column-matching step is the
 * only thing that gives a column its meaning.
 */

export interface SheetData {
  fileName: string;
  /** First row of the file, used as the column names. */
  headers: string[];
  /** Data rows, padded to `headers.length`, blank rows dropped. */
  rows: SheetRow[];
}

export interface SheetRow {
  /** 1-based line in the source file (header included), for error reporting. */
  rowNumber: number;
  cells: string[];
}

export class SpreadsheetError extends Error {
  constructor(public readonly reason: "unsupported" | "empty") {
    super(reason);
    this.name = "SpreadsheetError";
  }
}

/** Extensions offered in the file picker (kept in sync with `parseSpreadsheet`). */
export const SPREADSHEET_ACCEPT = ".csv,.tsv,.txt,.xlsx,.xlsm,text/csv";

export async function parseSpreadsheet(file: File): Promise<SheetData> {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  let table: string[][];

  if (extension === "xlsx" || extension === "xlsm") {
    // Loaded on demand: the xlsx reader is dead weight for everyone who never
    // imports a file. readSheet() reads the first sheet of the workbook.
    const { readSheet } = await import("read-excel-file/browser");
    const sheet = await readSheet(file);
    table = sheet.map((row) => row.map(cellToString));
  } else if (
    extension === "csv" ||
    extension === "tsv" ||
    extension === "txt"
  ) {
    table = parseDelimited(stripBom(await file.text()));
  } else {
    // Notably legacy .xls (BIFF) — the UI tells the driver to re-save as
    // .xlsx or CSV.
    throw new SpreadsheetError("unsupported");
  }

  const headerIndex = table.findIndex(isNotBlank);
  if (headerIndex < 0) throw new SpreadsheetError("empty");

  const headers = table[headerIndex].map((cell, index) =>
    cell.trim() ? cell.trim() : `#${index + 1}`,
  );

  const rows: SheetRow[] = [];
  for (let i = headerIndex + 1; i < table.length; i += 1) {
    const cells = table[i];
    if (!isNotBlank(cells)) continue;
    rows.push({
      rowNumber: i + 1,
      cells: headers.map((_, column) => cells[column]?.trim() ?? ""),
    });
  }
  if (rows.length === 0) throw new SpreadsheetError("empty");

  return { fileName: file.name, headers, rows };
}

const isNotBlank = (cells: string[] | undefined): boolean =>
  !!cells?.some((cell) => cell.trim() !== "");

const stripBom = (text: string): string =>
  text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

/** Excel gives back typed cells; dates keep the ISO date part, not the time. */
function cellToString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
}

/**
 * RFC 4180 CSV, with the delimiter sniffed from the header line so the
 * semicolon-separated files Excel writes on Arabic/European locales — and TSV —
 * work without asking.
 */
function parseDelimited(text: string): string[][] {
  const delimiter = detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === delimiter) endField();
    else if (char === "\n") endRow();
    else if (char === "\r")
      continue; // CRLF — the \n does the work
    else field += char;
  }
  if (field !== "" || row.length > 0) endRow();

  return rows;
}

function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, text.indexOf("\n") + 1 || undefined);
  const counts = [",", ";", "\t"].map((delimiter) => ({
    delimiter,
    count: firstLine.split(delimiter).length - 1,
  }));
  counts.sort((a, b) => b.count - a.count);
  return counts[0].count > 0 ? counts[0].delimiter : ",";
}
